import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MessengerReply } from '@/messenger/MessengerReply'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import type { Locale } from '@/i18n'
import { ApiError } from '@/lib/api'
import { asAmount, asCurrency } from '@/format/money'
import { formatDate, formatMoney, formatTotals } from '@/lib/format'
import { fetchPage } from '@/lists/api'
import { CellValue, valueKey } from '@/lists/cellValue'
import type { ListRow } from '@/lists/types'
import { postComment, searchMentionable } from '@/notifications/api'
import { createTask, logCall, mayLogActivity } from './activityApi'
import { TimelineMore, useTimeline } from './useTimeline'
import { AttachmentsPanel } from './AttachmentsPanel'
import { NewContactForm } from './ContactTabs'
import { contractsTab } from './ContractsPanel'
import {
  deleteCounterparty,
  fetchBankAccounts,
  fetchContacts,
  fetchFinance,
  fetchRequisites,
  fetchTimeline as fetchTouches,
  type AgeingLine,
  type BankAccount,
  type CardContact,
  type Finance,
  type Requisites,
  type Timeline,
} from './counterpartyApi'
import { duplicatesTab } from './CounterpartyMergeTab'
import { CreditPanel, interactionsTab } from './CounterpartyTabs'
import { CustomFieldsPanel } from './CustomFieldsPanel'
import { daysUntil } from './DealRecordView'
import { HistoryPanel } from './HistoryPanel'
import { fetchReceivable, type Invoice } from './invoiceApi'
import { ReceivablesPanel } from './ReceivablesPanel'
import {
  Composer,
  ConfirmModal,
  Facts,
  Feed,
  Panel,
  RecordHeader,
  RecordTabs,
  StateView,
  type ComposerMode,
  type Fact,
  type HeaderAction,
  type MenuAction,
} from './record'
import {
  CopyValue,
  feedItemOf,
  formatMoment,
  initials,
  labelOf,
  listPath,
  totalsByCurrency,
  type Translate,
} from './recordHelpers'
import type { RecordViewProps } from './types'
import '@/styles/record.css'
import { useWriteEmail } from '@/mail/WriteEmail'
import { useCallAction } from '@/telephony/CallButton'
import { ConversationSummaryPanel } from '@/ai/ConversationSummary'
import { TaskProposalsPanel } from '@/ai/TaskProposals'
import { ClientRiskPanel } from '@/risk/ClientRiskPanel'

/**
 * Карточка контрагента на дизайн-системе (Company.dc.html).
 *
 * Первое, что видно, — деньги под риском: строка просрочки над фактами, чтобы новую
 * отгрузку не начали, не заметив долг. Дальше — факты (открытые сделки, долг, последний
 * контакт, условия оплаты) и пять вкладок вместо прежних одиннадцати; прежнее содержимое
 * не потеряно, а распределено:
 *
 * - **Обзор** — открытые сделки, задолженность по счетам, композер и лента; справа люди,
 *   реквизиты (правка полей карточки), договоры кратко, доп. поля;
 * - **Сделки** — открытые сделки целиком;
 * - **Финансы** — кредитный лимит и блокировка (#70), дебиторка, счета и подписки (#72);
 * - **Документы** — договоры с действиями (#73), первичные документы, вложения;
 * - **История** — касания со счётчиком (#70) и журнал изменений (#104).
 *
 * Дубли и слияние (#99) — пунктом меню «⋯»: это действие над карточкой, а не её раздел, и
 * последний шаг слияния спрашивает подтверждения. Удаление — там же, за чертой.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - «Оборот за 12 мес.»: сервер не отдаёт выручку по клиенту за период — ни эндпоинтом
 *   карточки, ни витриной, читаемой от имени менеджера. Сложить её из счетов списка значило
 *   бы посчитать выставленное, а не полученное, и смешать валюты.
 * - «Выставить счёт» и «Акт сверки»: счёт требует организацию, которой у контрагента нет
 *   (та же причина, что у сделки), а эндпоинта акта сверки нет вовсе.
 * - «Отправить напоминание» в строке просрочки: напоминания шлёт обход дебиторки по
 *   расписанию (#27), ручного запуска по клиенту API не даёт.
 * - Город в мета-строке: отдельного поля нет, а резать его из адреса — догадка.
 * - Следующий шаг и полоса стадий в строке сделки: список сделок не несёт ни задач, ни
 *   порядка стадий; стадия показана названием.
 * - «Взвешенная» сумма открытых сделок: её считает сервер по сделке, в строке списка её
 *   нет, а своя формула на клиенте стала бы вторым ответом на вопрос прогноза.
 * - Роли людей «ЛПР / бухгалтер»: у контакта есть только должность; роли живут на участии в
 *   сделке, а не на человеке.
 */
export function CounterpartyRecordView({ recordId, record, context, form, notices, edit }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const mail = useWriteEmail({ type: 'counterparty', id: recordId })
  // Through the PBX (#122); no tel: fallback here — a company card has no single number to dial.
  const dial = useCallAction({ type: 'counterparty', id: recordId }, null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [tab, setTab] = useState('overview')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [duplicatesOpen, setDuplicatesOpen] = useState(false)
  const [allFields, setAllFields] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const mayReadInvoices = can(user, 'billing.invoice', 'read')
  const mayReadDeals = can(user, 'sales.deal', 'read')
  const mayReadContracts = can(user, 'billing.contract', 'read')
  const mayReadDocuments = can(user, 'billing.document', 'read')
  const mayUpdate = can(user, 'sales.counterparty', 'update')
  const mayDelete = can(user, 'sales.counterparty', 'delete')
  const mayCreateDeal = can(user, 'sales.deal', 'create')

  const requisites = useQuery({
    queryKey: ['counterparty-requisites', recordId],
    queryFn: ({ signal }) => fetchRequisites(recordId, signal),
    retry: false,
  })
  // Without the right to invoices the server answers 403 anyway: the request is not sent,
  // and the facts that need it are not drawn rather than drawn as zero debt.
  const finance = useQuery({
    queryKey: ['counterparty-finance', recordId],
    queryFn: ({ signal }) => fetchFinance(recordId, signal),
    enabled: mayReadInvoices,
    retry: false,
  })
  const receivable = useQuery({
    queryKey: ['counterparty-receivable', recordId],
    queryFn: ({ signal }) => fetchReceivable(recordId, signal),
    enabled: mayReadInvoices,
    retry: false,
  })
  const deals = useQuery({
    queryKey: ['counterparty-open-deals', recordId],
    queryFn: ({ signal }) => fetchOpenDeals(recordId, signal),
    enabled: mayReadDeals,
    retry: false,
  })
  const contacts = useQuery({
    queryKey: ['counterparty-contacts', recordId],
    queryFn: ({ signal }) => fetchContacts(recordId, signal),
    retry: false,
  })
  const accounts = useQuery({
    queryKey: ['counterparty-bank-accounts', recordId],
    queryFn: ({ signal }) => fetchBankAccounts(recordId, signal),
    retry: false,
  })
  const touches = useQuery({
    queryKey: ['counterparty-timeline', recordId],
    queryFn: ({ signal }) => fetchTouches(recordId, 50, signal),
    retry: false,
  })
  // The customer's whole history, paged back to the first touch (#114).
  const feed = useTimeline('counterparty', recordId)

  const remove = useMutation({
    mutationFn: () => deleteCounterparty(recordId),
    onSuccess: () => {
      setConfirmDelete(false)
      void queryClient.invalidateQueries({ queryKey: ['list', 'sales.counterparty'] })
      navigate(listPath('sales.counterparty', '/counterparties'))
    },
    // The server names what holds the card (open deals, issued invoices): its words, not ours.
    onError: (error) => {
      setConfirmDelete(false)
      setFailure(error instanceof ApiError ? error.message : t('app.error'))
    },
  })

  const refreshFeed = () => {
    void queryClient.invalidateQueries({ queryKey: ['timeline', 'counterparty', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['counterparty-timeline', recordId] })
  }

  const values = record.values
  const name = text(values.name) ?? requisites.data?.name ?? recordId
  const inn = text(values.inn) ?? requisites.data?.inn ?? null
  const kpp = text(values.kpp) ?? requisites.data?.kpp ?? null
  const relation = text(values.relation) ?? requisites.data?.relation ?? null
  const responsible = labelOf(values, 'responsible_employee_id')
  const blocked = requisites.data?.blocked ?? finance.data?.blocked ?? false
  const blockReason = requisites.data?.blockReason ?? finance.data?.blockReason ?? null

  // ------------------------------------------------------------------ шапка

  const meta = [
    relation ? (
      <CellValue
        key="relation"
        values={{ relation }}
        column="relation"
        descriptor={{ name: 'relation', type: 'text', sortable: false, filterable: false }}
        entity="sales.counterparty"
      />
    ) : null,
    blocked ? (
      <span key="blocked" className="badge b-danger">
        <Icon name="lock" />
        {t('record.cp.blocked')}
      </span>
    ) : null,
    inn ? (
      <CopyValue key="inn" value={inn} prefix={t('column.inn')} label={t('record.cp.copyInn', { value: inn })} />
    ) : null,
    kpp ? (
      <CopyValue key="kpp" value={kpp} prefix={t('column.kpp')} label={t('record.cp.copyKpp', { value: kpp })} />
    ) : null,
    responsible ? (
      <span key="responsible" className="row" style={{ gap: 6 }}>
        <span className="ava" aria-hidden="true">
          {initials(responsible)}
        </span>
        {responsible}
      </span>
    ) : (
      <span key="responsible" className="muted">
        {t('record.cp.noResponsible')}
      </span>
    ),
  ]

  const primary: HeaderAction | null = mayCreateDeal
    ? {
        id: 'new-deal',
        label: t('record.cp.newDeal'),
        icon: 'plus',
        onClick: () =>
          navigate(`${listPath('sales.deal', '/deals')}/new`, { state: { counterparty: { id: recordId, label: name } } }),
      }
    : null

  const menu: MenuAction[] = [
    {
      id: 'copy-link',
      label: t('record.copyLink'),
      icon: 'link',
      onSelect: () => {
        void navigator.clipboard?.writeText(window.location.href).catch(() => setFailure(t('record.copyFailed')))
      },
    },
    {
      id: 'duplicates',
      label: t('record.cp.duplicates'),
      icon: 'copy',
      onSelect: () => setDuplicatesOpen(true),
    },
  ]
  if (mayDelete) {
    menu.push({
      id: 'delete',
      label: t('record.cp.delete'),
      icon: 'trash',
      danger: true,
      onSelect: () => setConfirmDelete(true),
    })
  }

  // ------------------------------------------------------------------ деньги под риском

  const ageing = finance.data?.ageingByCurrency ?? []
  const overdueTotals = ageing
    .map((line) => ({ currency: line.currency, amount: overdueOf(line) }))
    .filter((line) => line.amount > 0)
  const overdueInvoices = (receivable.data?.outstanding ?? []).filter((invoice) => overdueDays(invoice) > 0)
  const oldestOverdue = overdueInvoices.reduce((max, invoice) => Math.max(max, overdueDays(invoice)), 0)

  // ------------------------------------------------------------------ факты

  const openDeals = deals.data?.rows ?? []
  const facts: Fact[] = []
  if (mayReadDeals) {
    facts.push({
      id: 'deals',
      label: t('record.cp.fact.deals'),
      value: deals.isLoading ? '…' : deals.isError ? '—' : `${openDeals.length}${deals.data?.hasMore ? '+' : ''}`,
      sub:
        openDeals.length > 0
          ? formatTotals(
              totalsByCurrency(openDeals, (row) => String(row.currency ?? ''), (row) => Number(row.amount ?? 0)),
              locale,
            )
          : t('record.cp.fact.noDeals'),
    })
  }
  if (mayReadInvoices) {
    const debt = ageing.map((line) => ({ currency: line.currency, amount: line.current + overdueOf(line) }))
    facts.push({
      id: 'debt',
      label: t('record.cp.fact.debt'),
      value: finance.isLoading ? '…' : finance.isError ? '—' : debt.length === 0 ? t('record.cp.fact.noDebt') : formatTotals(debt, locale),
      sub:
        overdueTotals.length > 0 ? (
          <span className="flag flag-danger">
            <Icon name="alert" />
            {t('record.cp.fact.overdue', { amount: formatTotals(overdueTotals, locale) })}
          </span>
        ) : finance.isSuccess && debt.length > 0 ? (
          t('record.cp.fact.noOverdue')
        ) : undefined,
    })
  }
  const lastTouch = lastContactOf(touches.data)
  facts.push({
    id: 'last-contact',
    label: t('record.cp.fact.lastContact'),
    value: lastTouch ? formatMoment(lastTouch.at, locale) : touches.isLoading ? '…' : '—',
    sub: lastTouch
      ? [t(`activity.type.${lastTouch.type}`), lastTouch.subject].filter(Boolean).join(': ')
      : touches.isSuccess
        ? t('record.cp.fact.noContact')
        : undefined,
  })
  const terms = requisites.data?.paymentTermsDays ?? finance.data?.paymentTermsDays
  if (terms !== undefined) {
    facts.push({
      id: 'terms',
      label: t('record.cp.fact.terms'),
      value: terms === 0 ? t('record.cp.fact.prepayment') : t('record.cp.fact.deferral', { days: terms }),
      sub: finance.data ? creditSub(finance.data, locale, t) : undefined,
    })
  }

  // ------------------------------------------------------------------ композер и лента

  const mayComment = can(user, 'system.comment', 'create')
  const modes: ComposerMode[] = []
  if (mayComment) {
    modes.push({
      id: 'comment',
      label: t('timeline.kind.comment'),
      icon: 'comment',
      placeholder: t('record.cp.composer.comment'),
      mentions: searchMentionable,
      // Comments are keyed by the catalogue name, activities by the short code (#79).
      submit: async ({ text: body, mentionedUserIds }) => {
        await postComment('sales.counterparty', recordId, body, { mentionedUserIds })
        refreshFeed()
      },
    })
  }
  if (mayUpdate && mayLogActivity(user) && user?.employeeId) {
    const employeeId = user.employeeId
    modes.push({
      id: 'call',
      label: t('timeline.kind.call'),
      icon: 'phone',
      placeholder: t('record.composer.call'),
      extra: 'duration',
      submit: async ({ text: subject, durationMinutes }) => {
        await logCall({
          entityType: 'counterparty',
          entityId: recordId,
          subject,
          direction: 'outbound',
          durationSeconds: durationMinutes === undefined ? null : Math.round(durationMinutes * 60),
          employeeId,
        })
        refreshFeed()
      },
    })
    modes.push({
      id: 'task',
      label: t('timeline.kind.task'),
      icon: 'work',
      placeholder: t('record.composer.task'),
      extra: 'due',
      submit: async ({ text: subject, due }) => {
        await createTask({
          entityType: 'counterparty',
          entityId: recordId,
          subject,
          assigneeId: employeeId,
          dueAt: due ? new Date(due).toISOString() : null,
        })
        refreshFeed()
      },
    })
  }

  const feedItems = (feed.data?.events ?? []).map((event) => feedItemOf(event, t))
  const feedView = feed.isLoading ? (
    <p className="muted">{t('app.loading')}</p>
  ) : feed.isError ? (
    <StateView
      compact
      kind={feed.error instanceof ApiError && feed.error.isForbidden ? 'forbidden' : 'error'}
      title={t('record.cp.feed.error')}
    />
  ) : (
    <>
      <TaskProposalsPanel entityType="counterparty" entityId={recordId} />
      <ConversationSummaryPanel entityType="counterparty" entityId={recordId} />
    <Feed
      items={feedItems}
      title={t('record.cp.feed.title')}
      empty={<StateView compact kind="empty" title={t('record.cp.feed.empty.title')} description={t('record.cp.feed.empty.text')} />}
      footer={<TimelineMore feed={feed} />}
    />
    </>
  )

  // ------------------------------------------------------------------ панели

  const dealsPanel = (limit: number | null) =>
    mayReadDeals ? (
      <Panel
        title={t('record.cp.openDeals')}
        count={deals.data ? openDeals.length : null}
        flush
        actions={
          limit !== null && openDeals.length > limit ? (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTab('deals')}>
              {t('record.cp.allDeals')}
            </button>
          ) : undefined
        }
      >
        <OpenDealsTable query={deals} limit={limit} onNewDeal={primary?.onClick} />
      </Panel>
    ) : null

  const overview = (
    <div className="layout-2-wide">
      <div className="rec-col">
        {dealsPanel(5)}

        {mayReadInvoices && (
          <Panel
            title={t('record.cp.debt')}
            flush
            actions={
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTab('finance')}>
                {t('record.cp.allInvoices')}
              </button>
            }
          >
            <DebtPanel ageing={ageing} receivable={receivable} financeFailed={finance.isError} />
          </Panel>
        )}

        {modes.length > 0 && <Composer modes={modes} />}
        {/* The answer into the client's Telegram or WhatsApp chat (#125). */}
        <MessengerReply entityType="counterparty" recordId={recordId} />
        {feedView}
      </div>

      <div className="rec-col">
        <Panel title={t('record.cp.people')} count={contacts.data?.length ?? null} flush>
          <PeopleList query={contacts} />
          <div className="panel-b">
            <NewContactForm counterpartyId={recordId} />
          </div>
        </Panel>

        <Panel
          title={t('record.cp.requisites')}
          actions={
            edit.editing ? (
              <>
                <button type="button" className="btn btn-ghost btn-sm" onClick={edit.cancel} disabled={edit.saving}>
                  {t('card.cancel')}
                </button>
                <button type="button" className="btn btn-primary btn-sm" onClick={edit.submit} disabled={edit.saving}>
                  {edit.saving ? t('card.saving') : t('card.save')}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  aria-expanded={allFields}
                  onClick={() => setAllFields((value) => !value)}
                >
                  {allFields ? t('record.cp.fewerFields') : t('record.cp.allFields')}
                </button>
                {edit.mayUpdate && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={edit.begin}>
                    {t('card.edit')}
                  </button>
                )}
              </>
            )
          }
        >
          {edit.editing || allFields ? (
            form
          ) : (
            <RequisitesSummary requisites={requisites} accounts={accounts.data ?? []} />
          )}
        </Panel>

        {mayReadInvoices && finance.data && (
          <Panel
            title={t('record.cp.contracts')}
            count={finance.data.contracts.length}
            flush
            actions={
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTab('documents')}>
                {t('record.cp.allDocuments')}
              </button>
            }
          >
            <ContractsSummary finance={finance.data} />
          </Panel>
        )}

        <Panel title={t('card.tab.customFields')}>
          <CustomFieldsPanel entity={context.entity} recordId={recordId} resource={context.resource} />
        </Panel>
      </div>
    </div>
  )

  const financeTab = (
    <div className="rec-col" style={{ paddingTop: 14 }}>
      <Panel title={t('record.cp.credit')}>
        {!mayReadInvoices ? (
          <p className="muted">{t('card.cp.finance.needsBilling')}</p>
        ) : finance.isLoading ? (
          <p className="muted">{t('app.loading')}</p>
        ) : finance.isError || !finance.data ? (
          <StateView compact kind="error" title={t('record.cp.finance.error')} />
        ) : (
          <CreditPanel finance={finance.data} />
        )}
      </Panel>
      {mayReadInvoices && (
        <Panel title={t('card.tab.receivables')}>
          <ReceivablesPanel recordId={recordId} />
        </Panel>
      )}
    </div>
  )

  const documentsTab = (
    <div className="rec-col" style={{ paddingTop: 14 }}>
      {mayReadContracts && <Panel title={t('card.tab.contracts')}>{contractsTab().render(context)}</Panel>}
      {mayReadDocuments && (
        <Panel title={t('record.cp.primaryDocuments')} flush>
          <PrimaryDocuments counterpartyId={recordId} />
        </Panel>
      )}
      <Panel title={t('card.tab.attachments')}>
        <AttachmentsPanel entityType={context.entity} entityId={recordId} resource={context.resource} />
      </Panel>
    </div>
  )

  const historyTab = (
    <div className="rec-col" style={{ paddingTop: 14 }}>
      <Panel title={t('card.tab.interactions')}>{interactionsTab().render(context)}</Panel>
      <Panel title={t('record.history.changes')}>
        <HistoryPanel entity={context.entity} recordId={recordId} />
      </Panel>
    </div>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.counterparties'), to: listPath('sales.counterparty', '/counterparties') }, { label: name }]}
        title={name}
        meta={meta}
        primary={primary}
        secondary={[dial.action, mail.action].filter((action): action is HeaderAction => action != null)}
        menu={menu}
        menuLabel={t('record.more')}
      />
      {mail.dialog}
      {dial.outcome}

      {failure && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{failure}</span>
        </div>
      )}
      {notices}

      {blocked && (
        <div className="alert alert-danger" role="status">
          <Icon name="lock" />
          <span className="fg">{t('card.cp.blocked', { reason: blockReason ?? '—' })}</span>
        </div>
      )}
      {overdueTotals.length > 0 && (
        <div className="alert alert-danger" role="status">
          <Icon name="clock" />
          <span>
            <span className="strong">{t('record.cp.overdueAlert', { amount: formatTotals(overdueTotals, locale) })}</span>{' '}
            {overdueInvoices.length > 0 &&
              t('record.cp.overdueAlertDetails', { count: overdueInvoices.length, days: oldestOverdue })}
          </span>
        </div>
      )}
      {/* Client risk (#135) next to the money at risk: it is read before a new order is taken. */}
      <ClientRiskPanel counterpartyId={recordId} />
      {finance.data?.creditState === 'exceeded' && (
        <div className="alert alert-warning" role="status">
          <Icon name="alert" />
          <span>
            {t('card.cp.credit.exceeded', {
              debt: formatMoney(finance.data.outstandingDebt, null, locale),
              limit: formatMoney(finance.data.creditLimit ?? 0, null, locale),
            })}
          </span>
        </div>
      )}

      {duplicatesOpen && (
        <Panel
          title={t('record.cp.duplicates')}
          actions={
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDuplicatesOpen(false)}>
              {t('record.cp.close')}
            </button>
          }
        >
          {duplicatesTab().render(context)}
        </Panel>
      )}

      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'overview', label: t('record.tab.overview') },
          ...(mayReadDeals ? [{ id: 'deals', label: t('record.cp.tab.deals'), count: deals.data ? openDeals.length : null }] : []),
          { id: 'finance', label: t('record.cp.tab.finance') },
          { id: 'documents', label: t('record.tab.documents') },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.cp.sections')}
      >
        {tab === 'overview' && <div style={{ paddingTop: 14 }}>{overview}</div>}
        {tab === 'deals' && <div className="rec-col" style={{ paddingTop: 14 }}>{dealsPanel(null)}</div>}
        {tab === 'finance' && financeTab}
        {tab === 'documents' && documentsTab}
        {tab === 'history' && historyTab}
      </RecordTabs>

      <ConfirmModal
        open={confirmDelete}
        title={t('record.cp.delete.title', { name })}
        confirmLabel={t('record.cp.delete')}
        pending={remove.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
      >
        <p>{t('record.cp.delete.text')}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.irreversible')}
        </p>
      </ConfirmModal>
    </div>
  )
}

// ------------------------------------------------------------------ данные

/**
 * Открытые сделки клиента — механизмом списков, а не своим запросом: там уже работают
 * область видимости и полевые права, и менеджер увидит здесь ровно те сделки, что и в
 * списке. Свой эндпоинт «сделки контрагента» стал бы вторым местом, где это решается.
 */
function fetchOpenDeals(counterpartyId: string, signal?: AbortSignal) {
  return fetchPage(
    'sales.deal',
    {
      columns: ['number', 'title', 'stage_id', 'amount', 'currency', 'expected_close_on'],
      filters: [
        { column: 'counterparty_id', operator: 'eq', value: counterpartyId },
        { column: 'status', operator: 'eq', value: 'open' },
      ],
      sort: [{ column: 'expected_close_on', descending: false }],
      pageSize: 50,
    },
    null,
    signal,
  )
}

/** Первичные документы клиента — тем же механизмом списков (область видимости — его). */
function fetchDocumentsOf(counterpartyId: string, signal?: AbortSignal) {
  return fetchPage(
    'billing.document',
    {
      columns: ['number', 'document_kind', 'document_date', 'total', 'currency', 'status'],
      filters: [{ column: 'counterparty_id', operator: 'eq', value: counterpartyId }],
      sort: [{ column: 'document_date', descending: true }],
      pageSize: 20,
    },
    null,
    signal,
  )
}

// ------------------------------------------------------------------ части

type QueryOf<T> = { isLoading: boolean; isError: boolean; error: unknown; data: T | undefined }

function OpenDealsTable({
  query,
  limit,
  onNewDeal,
}: {
  query: QueryOf<{ rows: ListRow[]; hasMore: boolean }>
  limit: number | null
  onNewDeal?: () => void
}) {
  const { t, locale } = useApp()
  if (query.isLoading) return <div className="panel-b muted">{t('app.loading')}</div>
  if (query.isError) return <StateView compact kind={kindOf(query.error)} title={t('record.cp.deals.error')} />
  const rows = query.data?.rows ?? []
  if (rows.length === 0) {
    return (
      <StateView
        compact
        kind="empty"
        title={t('record.cp.deals.empty.title')}
        description={t('record.cp.deals.empty.text')}
        actions={
          onNewDeal ? (
            <button type="button" className="btn btn-secondary btn-sm" onClick={onNewDeal}>
              {t('record.cp.newDeal')}
            </button>
          ) : undefined
        }
      />
    )
  }
  const shown = limit === null ? rows : rows.slice(0, limit)
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="tbl" aria-label={t('record.cp.openDeals')}>
        <thead>
          <tr>
            <th>{t('record.cp.deal')}</th>
            <th>{t('column.stage_id')}</th>
            <th className="num">{t('column.amount')}</th>
            <th>{t('column.expected_close_on')}</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => {
            const due = typeof row.expected_close_on === 'string' ? row.expected_close_on : null
            const days = due ? daysUntil(due) : null
            return (
              <tr key={row.id}>
                <td className="wrap">
                  <span className="stack" style={{ gap: 0 }}>
                    <Link to={`${listPath('sales.deal', '/deals')}/${row.id}`}>{String(row.title ?? row.number ?? '')}</Link>
                    <span className="mono muted t-xs">{String(row.number ?? '')}</span>
                  </span>
                </td>
                <td>{labelOf(row, 'stage_id') ?? '—'}</td>
                <td className="num">
                  {/* No currency, no currency sign: guessing roubles would state more than is known. */}
                  {formatMoney(asAmount(row.amount), asCurrency(row.currency), locale)}
                </td>
                <td>
                  {due ? formatDate(due, locale) : '—'}
                  {/* A missed close date is a word and an icon, not a red cell. */}
                  {days !== null && days < 0 && (
                    <span className="flag flag-warning" style={{ marginLeft: 6 }}>
                      <Icon name="alert" />
                      {t('record.deal.overdueBy', { days: -days })}
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {query.data?.hasMore && limit === null && <div className="panel-f muted">{t('record.cp.deals.more')}</div>}
    </div>
  )
}

/**
 * Задолженность: полоса «в срок / просрочено» по каждой валюте и открытые счета со сроком
 * словами. Полоса — на валюту: одна полоса на рубли и евро сравнивала бы их длиной, то есть
 * по курсу один к одному.
 */
function DebtPanel({
  ageing,
  receivable,
  financeFailed,
}: {
  ageing: AgeingLine[]
  receivable: QueryOf<{ outstanding: Invoice[] }>
  financeFailed: boolean
}) {
  const { t, locale } = useApp()
  if (receivable.isLoading) return <div className="panel-b muted">{t('app.loading')}</div>
  if (receivable.isError || financeFailed) {
    return <StateView compact kind={kindOf(receivable.error)} title={t('record.cp.debt.error')} />
  }
  const invoices = receivable.data?.outstanding ?? []
  if (invoices.length === 0 && ageing.length === 0) {
    return <div className="panel-b secondary">{t('record.cp.debt.none')}</div>
  }
  return (
    <>
      {ageing.length > 0 && (
        <div className="panel-b stack" style={{ gap: 8, borderBottom: '1px solid var(--border)' }}>
          {ageing.map((line) => {
            const overdue = overdueOf(line)
            return (
              <div key={line.currency} className="stack" style={{ gap: 6 }}>
                <div
                  className="stackbar"
                  role="img"
                  aria-label={t('record.cp.debt.bar', {
                    current: formatMoney(line.current, line.currency, locale),
                    overdue: formatMoney(overdue, line.currency, locale),
                  })}
                >
                  {line.current > 0 && <i style={{ flex: line.current, background: 'var(--seq-0)' }} />}
                  {overdue > 0 && <i style={{ flex: overdue, background: 'var(--seq-2)' }} />}
                </div>
                <div className="row-wrap t-sm" style={{ gap: 16 }}>
                  <span className="row" style={{ gap: 6 }}>
                    <span className="legend-sw" style={{ background: 'var(--seq-0)' }} />
                    {t('record.cp.debt.current', { amount: formatMoney(line.current, line.currency, locale) })}
                  </span>
                  <span className="row" style={{ gap: 6 }}>
                    <span className="legend-sw" style={{ background: 'var(--seq-2)' }} />
                    {t('record.cp.debt.overdue', { amount: formatMoney(overdue, line.currency, locale) })}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}
      {invoices.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl" aria-label={t('record.cp.debt')}>
            <thead>
              <tr>
                <th>{t('record.cp.invoice')}</th>
                <th>{t('column.issued_on')}</th>
                <th>{t('column.due_on')}</th>
                <th className="num">{t('card.inv.total.total')}</th>
                <th className="num">{t('card.inv.outstanding')}</th>
                <th>{t('column.status')}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <Link to={`${listPath('billing.invoice', '/invoices')}/${invoice.id}`} className="mono">
                      {invoice.number}
                    </Link>
                  </td>
                  <td>{formatDate(invoice.issuedOn, locale)}</td>
                  <td>{invoice.dueOn ? formatDate(invoice.dueOn, locale) : '—'}</td>
                  <td className="num">{formatMoney(invoice.total, invoice.currency, locale)}</td>
                  <td className="num strong">{formatMoney(invoice.outstanding, invoice.currency, locale)}</td>
                  <td>
                    <DueBadge invoice={invoice} />
                  </td>
                </tr>
              ))}
              <tr className="total">
                <td>{t('card.deal.lines.total')}</td>
                <td />
                <td />
                <td />
                <td className="num">
                  {formatTotals(totalsByCurrency(invoices, (invoice) => invoice.currency, (invoice) => invoice.outstanding), locale)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

/** Срок счёта словом и иконкой: «Просрочен 18 дн.» / «Ждёт оплаты · 7 дн.». */
export function DueBadge({ invoice }: { invoice: Pick<Invoice, 'dueOn' | 'status' | 'outstanding'> }) {
  const { t } = useApp()
  const late = overdueDays(invoice)
  if (late > 0) {
    return (
      <span className="badge b-danger">
        <Icon name="clock" />
        {t('record.inv.overdueDays', { days: late })}
      </span>
    )
  }
  const left = invoice.dueOn ? daysUntil(invoice.dueOn) : null
  return (
    <span className="badge b-info">
      <span className="dot" aria-hidden="true" />
      {left === null ? t('record.inv.awaiting') : t('record.inv.awaitingDays', { days: left })}
    </span>
  )
}

function PeopleList({ query }: { query: QueryOf<CardContact[]> }) {
  const { t } = useApp()
  if (query.isLoading) return <div className="panel-b muted">{t('app.loading')}</div>
  if (query.isError) return <StateView compact kind={kindOf(query.error)} title={t('record.cp.people.error')} />
  const people = query.data ?? []
  if (people.length === 0) return <div className="panel-b secondary">{t('card.cp.contacts.empty')}</div>
  return (
    <>
      {people.map((person) => {
        const phone = person.mobilePhone ?? person.phone
        return (
          <div key={person.id} className="list-item" style={{ alignItems: 'flex-start' }}>
            <span className="ava ava-lg" aria-hidden="true">
              {initials(person.fullName)}
            </span>
            <div className="stack" style={{ gap: 0, flex: 1, minWidth: 0 }}>
              <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <Link to={`${listPath('sales.contact', '/contacts')}/${person.id}`}>{person.fullName}</Link>
                {/* "Primary" is a word, not boldness: it survives print and colour blindness. */}
                {person.primary && <span className="badge b-accent">{t('card.cp.contact.primary')}</span>}
              </span>
              {person.jobTitle && <span className="t-sm secondary">{person.jobTitle}</span>}
              {(phone || person.email) && (
                <span className="t-sm muted">{[phone, person.email].filter(Boolean).join(' · ')}</span>
              )}
              {/* Calling someone without consent to process personal data is not allowed,
                  and silence about it moves the breach onto whoever opened the card. */}
              {!person.consentGiven && (
                <span className="flag flag-warning t-sm">
                  <Icon name="alert" />
                  {t('card.cp.contact.noConsentFlag')}
                </span>
              )}
            </div>
            <div className="row" style={{ gap: 2 }}>
              {phone && (
                <a className="btn btn-ghost btn-sm btn-icon" href={`tel:${phone}`} aria-label={t('record.cp.call', { name: person.fullName })}>
                  <Icon name="phone" size="sm" />
                </a>
              )}
              {person.email && (
                <a
                  className="btn btn-ghost btn-sm btn-icon"
                  href={`mailto:${person.email}`}
                  aria-label={t('record.cp.write', { name: person.fullName })}
                >
                  <Icon name="mail" size="sm" />
                </a>
              )}
            </div>
          </div>
        )
      })}
    </>
  )
}

/** A counterparty code in words; an untranslated code is shown as is, not as a blank. */
function translatedCode(t: (key: string) => string, column: string, code: string): string {
  const key = valueKey('sales.counterparty', column, code)
  const text = t(key)
  return text === key ? code : text
}

function RequisitesSummary({ requisites, accounts }: { requisites: QueryOf<Requisites>; accounts: BankAccount[] }) {
  const { t } = useApp()
  if (requisites.isLoading) return <p className="muted">{t('app.loading')}</p>
  if (requisites.isError || !requisites.data) {
    return <StateView compact kind={kindOf(requisites.error)} title={t('record.cp.requisites.error')} />
  }
  const data = requisites.data
  const rows: [string, React.ReactNode][] = []
  if (data.legalName) rows.push([t('column.legal_name'), data.legalName])
  // Kind and relation in words (#90): the stored codes `legal`/`both` mean nothing to a reader.
  rows.push([t('column.kind'), translatedCode(t, 'kind', data.kind)])
  if (data.relation) rows.push([t('column.relation'), translatedCode(t, 'relation', data.relation)])
  if (data.inn || data.kpp) rows.push([t('record.cp.innKpp'), <span className="mono">{`${data.inn ?? '—'} / ${data.kpp ?? '—'}`}</span>])
  if (data.ogrn) rows.push([t('column.ogrn'), <span className="mono">{data.ogrn}</span>])
  if (data.okpo) rows.push([t('column.okpo'), <span className="mono">{data.okpo}</span>])
  if (data.legalAddress) rows.push([t('column.legal_address'), data.legalAddress])
  if (data.actualAddress && data.actualAddress !== data.legalAddress) rows.push([t('column.actual_address'), data.actualAddress])
  if (data.phone) rows.push([t('column.phone'), data.phone])
  if (data.email) rows.push([t('column.email'), data.email])
  if (data.website) rows.push([t('column.website'), data.website])
  for (const account of accounts) {
    rows.push([
      t('card.cp.account.bank'),
      <>
        {account.bankName}, {t('card.cp.account.bik')} <span className="mono">{account.bik}</span>
        {account.primary && <span className="badge" style={{ marginLeft: 6 }}>{t('card.cp.account.primary')}</span>}
      </>,
    ])
    // The full number, not the mask: a payment cannot be reconciled against a mask.
    // money-format-ok: an account number beside its currency, not a sum.
    rows.push([t('card.cp.account.number'), <span className="mono">{`${account.accountNumber} ${account.currency}`}</span>])
  }
  if (rows.length === 0) return <p className="secondary">{t('record.cp.requisites.empty')}</p>
  return (
    <dl className="kv" style={{ gridTemplateColumns: '112px minmax(0, 1fr)' }}>
      {rows.map(([label, value], index) => (
        <div key={index} style={{ display: 'contents' }}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function ContractsSummary({ finance }: { finance: Finance }) {
  const { t, locale } = useApp()
  if (finance.contracts.length === 0) {
    return <div className="panel-b secondary">{t('record.cp.contracts.none')}</div>
  }
  return (
    <>
      {finance.contracts.map((contract) => (
        <div key={contract.id} className="list-item">
          <div className="stack" style={{ flex: 1, gap: 0 }}>
            <span>{t('record.cp.contract', { number: contract.number })}</span>
            <span className="t-xs muted">
              {[
                contract.validUntil ? t('record.cp.validUntil', { date: formatDate(contract.validUntil, locale) }) : t('card.contract.openEnded'),
                t('card.cp.credit.termsDays', { days: contract.paymentTermsDays }),
              ].join(' · ')}
            </span>
          </div>
          {/* Status and usability are different statements: "active" past its end date
              still looks valid by status, and that is the gap to show. */}
          {contract.status === 'active' && !contract.usableToday ? (
            <span className="flag flag-warning">
              <Icon name="alert" />
              {t('card.cp.contract.lapsed')}
            </span>
          ) : (
            <span className={contract.status === 'active' ? 'badge b-success' : 'badge'}>{t(`contract.status.${contract.status}`)}</span>
          )}
        </div>
      ))}
    </>
  )
}

function PrimaryDocuments({ counterpartyId }: { counterpartyId: string }) {
  const { t, locale } = useApp()
  const query = useQuery({
    queryKey: ['counterparty-documents', counterpartyId],
    queryFn: ({ signal }) => fetchDocumentsOf(counterpartyId, signal),
    retry: false,
  })
  if (query.isLoading) return <div className="panel-b muted">{t('app.loading')}</div>
  if (query.isError) return <StateView compact kind={kindOf(query.error)} title={t('record.cp.documents.error')} />
  const rows = query.data?.rows ?? []
  if (rows.length === 0) return <div className="panel-b secondary">{t('record.cp.documents.empty')}</div>
  return (
    <>
      {rows.map((row) => (
        <div key={row.id} className="list-item">
          <Icon name="file" className="muted" />
          <div className="stack" style={{ flex: 1, gap: 0 }}>
            <Link to={`${listPath('billing.document', '/documents')}/${row.id}`} className="mono">
              {String(row.number ?? '')}
            </Link>
            <span className="t-xs muted">
              {[
                typeof row.document_date === 'string' ? formatDate(row.document_date, locale) : null,
                typeof row.total === 'number' && typeof row.currency === 'string' ? formatMoney(row.total, row.currency, locale) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          <CellValue
            values={row}
            column="status"
            descriptor={{ name: 'status', type: 'text', sortable: false, filterable: false }}
            entity="billing.document"
          />
        </div>
      ))}
    </>
  )
}

// ------------------------------------------------------------------ расчёты для показа

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function kindOf(error: unknown): 'forbidden' | 'error' {
  return error instanceof ApiError && error.isForbidden ? 'forbidden' : 'error'
}

/** Просроченное одной валюты: все корзины, кроме «срок не наступил». */
function overdueOf(line: AgeingLine): number {
  return line.upTo30Days + line.upTo60Days + line.upTo90Days + line.beyond90Days
}

/**
 * Дней просрочки счёта по его сроку; 0 — не просрочен.
 *
 * Только для показа срока словом. Сумму просрочки отсюда не считаем: её даёт ageing
 * сервера, а сумма по этой дате стала бы вторым ответом на тот же денежный вопрос.
 */
export function overdueDays(invoice: Pick<Invoice, 'dueOn' | 'status' | 'outstanding'>): number {
  if (!invoice.dueOn || invoice.outstanding <= 0) return 0
  if (invoice.status === 'paid' || invoice.status === 'cancelled' || invoice.status === 'draft') return 0
  const days = daysUntil(invoice.dueOn)
  return days !== null && days < 0 ? -days : 0
}

/** Последнее касание: звонок, письмо или встреча — задача касанием клиента не является. */
function lastContactOf(timeline: Timeline | undefined): { at: string; type: string; subject: string | null } | null {
  const touches = (timeline?.activities ?? [])
    .filter((activity) => activity.type === 'call' || activity.type === 'email' || activity.type === 'meeting')
    .map((activity) => ({ at: activity.completedAt ?? activity.createdAt, type: activity.type, subject: activity.subject }))
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
  return touches[0] ?? null
}

/**
 * Лимит под условиями оплаты. У лимита нет валюты на сервере, поэтому число без знака
 * валюты: приписать ему рубли значит сказать больше, чем известно.
 */
function creditSub(finance: Finance, locale: Locale, t: Translate): string {
  if (finance.unlimitedCredit) return t('card.cp.credit.unlimited')
  if (finance.creditLimit === null) return t('record.cp.fact.noLimit')
  return t('record.cp.fact.limit', { amount: formatMoney(finance.creditLimit, null, locale) })
}
